<template>
  <div
    :key="keyReset"
    :class="{ wrap: schema.wrap }" >
    <div class="contact-message flex">
      <SBIcon
        class="contact-avatar"
        :icon="SBIconList.PROFILE_FILLED_CIRCLE"
        :color="SBColors.blackUltralight"
        :size="18" />
      <Skeleton
        class="no-shrink"
        :placeholder="schema.contactMessage || 'Contact message'"
        :hide-mock="!!schema.contactMessage"
        :top-radius="15"
        :bottom-radius="15"
        :shadow="false"
        :width="75" />
    </div>
    <div class="AI-message flex">
      <span
        class="AI-avatar"
        :class="[avatarImage]" />
      <div
        class="AI-input">
        <Skeleton
          v-if="!schema.edit"
          class="no-shrink"
          :placeholder="schema.value || 'AI-generated message'"
          :top-radius="15"
          :bottom-radius="buttons && buttons.data && buttons.data.length ? 10 : 15"
          mock-color="#e1e6e8"
          background-color="white"
          :shadow="true" />
        <ValidationObserver
          v-else
          ref="textareaObserver"
          :style="cssVars" >
          <ValidationProvider
            ref="textareaProvider"
            v-slot="{ errors, failedRules }"
            :name="schema.title"
            :rules="validatorsDinamic">
            <SBTextInput
              ref="messageArea"
              class="border-15"
              :class="{ 'border-bottom-10': buttons && buttons.data && buttons.data.length }"
              :focus-shadow="true"
              :placeholder="placeholder"
              :show-tags-select="false"
              :hide-tools="schema.hideTools"
              hide-send-button
              :no-tags="schema.noTags"
              @input="textValueSync"
              @raw-input="rawInputSync" />
            <div
              v-if="schema.subtitle && !errors[0]"
              class="subtitle flex flex-align-center">
              <svg
                v-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string' && schema.subtitlePrefixIcon.endsWith('.svg')"
                class="mr-1 flex-start-image"
                :data-src="schema.subtitlePrefixIcon"
                fill="currentColor"
                width="10"
                height="10" />
              <img
                v-else-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string'"
                :src="schema.subtitlePrefixIcon"
                style="width: 10px; height: 10px;"
                class="mr-1 flex-start-image"
              >
              <SBIcon
                v-else-if="!schema.noSubtitlePrefixIcon"
                class="mr-1"
                :icon="SBIconList.INFO"
                :color="SBColors.greyLight"
                :size="10" />
              <span>{{ schema.subtitle }}</span>
            </div>
            <div
              v-if="errors[0]"
              class="error flex flex-align-center"
              :class="{ warning: warningValidators.includes(Object.keys(failedRules)[0])}">
              <SBIcon
                class="mr-1"
                :icon="warningValidators.includes(Object.keys(failedRules)[0]) ? SBIconList.INFO : SBIconList.ERROR"
                :color="warningValidators.includes(Object.keys(failedRules)[0]) ? '#DEBD30' : SBColors.redPrimary"
                :size="10" />
              <span>{{ schema.validatorMessages && schema.validatorMessages[Object.keys(failedRules)[0]] ? schema.validatorMessages[Object.keys(failedRules)[0]] : errors[0] }}</span>
            </div>
          </ValidationProvider>
        </ValidationObserver>
        <!-- buttons -->
        <NMBRButtons
          v-if="schema.buttons"
          ref="buttonsRef"
          v-model="buttons"
          class="conversation-button-list"
          style="margin-top: 8px; display: block;"
          :placeholder="schema.buttons.placeholder"
          :schema="{...schema.buttons, stepID: schema.stepID}"
          center />
        <NMBRActionsWrapper
          v-if="!schema.buttons && schema.actions"
          ref="actionsRef"
          v-model="actions"
          close-btn-right
          style="margin-top: 8px; display: block;"
          :schema="{...schema.actions, stepID: schema.stepID}"
          center />
      </div>
    </div>
  </div>
</template>

<script>
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBTextInput from '@/components/business/widgets/SBTextInput';
import { whatsappFormatMessage } from '@/helpers/stringHelpers';
import Skeleton from './Skeleton.form';
import NMBRButtons from './NMBRButtonsWrapper';
import NMBRActionsWrapper from './NMBRActionsWrapper.form';

export default {
  name: 'NMBRConversationFormWrapper',
  components: {
    SBIcon,
    SBTextInput,
    Skeleton,
    NMBRButtons,
    NMBRActionsWrapper,
  },
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    validators: {
      type: String,
      default: '',
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      textValue: '',
      rawInput: '',
      SBColors,
      SBIconList,
      buttons: [],
      actions: [],
      avatarImage: '',
      toggled: false,
      keyReset: 0,
      toggleBackup: {
        textValue: '',
        buttons: [],
        actions: [],
      },
      preventEmit: false,
    };
  },
  computed: {
    cssVars() {
      return {
        '--height': `${this.schema.height}px`,
      };
    },
    validatorsDinamic() {
      return !this.wrapInToggle ?
        this.validators :
        this.validators.split('|').filter(v => v === 'required').length ?
          this.validators :
          `${this.validators}|required`;
    },
    warningValidators() {
      return this.schema.warningValidators ? this.schema.warningValidators.split(',') : [];
    },
    wrapInToggle() {
      return Object.hasOwn(this.schema, 'toggle');
    },
  },
  watch: {
    // rawInput: {
    //   handler() {
    //     this.emitData();
    //   },
    //   immediate: true,
    // },
    textValue: {
      handler() {
        this.emitData();
      },
      immediate: true,
    },
    buttons: {
      handler() {
        this.emitData();
      },
      immediate: true,
    },
    actions: {
      handler() {
        this.emitData();
      },
      immediate: true,
    },
  },
  mounted() {
    this.avatarImage = localStorage.getItem('formSchemaAvatar') || '';
    if (this.schema.value && this.$refs.messageArea) {
      this.$refs.messageArea.setText(this.schema.value);
    }
    if (this.schema.toggle) {
      this.toggled = this.schema.toggle;
    }
    window.eventBus.$on('formSchemaAvatar', (image) => {
      this.avatarImage = image;
      localStorage.setItem('formSchemaAvatar', image);
    });
    window.eventBus.$on('resetValue', (fieldName = null) => {
      // if (!this.toggled && (!fieldName || fieldName === this.schema.returnKey)) {}
    });
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (this.$refs.textareaObserver) this.$refs.textareaObserver.validate();
      });
    }
    setTimeout(() => {
      this.preventEmit = false;
      this.emitData(true);
      if (this.schema.value && this.$refs.textareaObserver) {
        if (this.$refs.textareaObserver) this.$refs.textareaObserver.validate();
      }
    }, 0);
  },
  methods: {
    textValueSync(textValue) {
      this.textValue = textValue;
    },
    rawInputSync(rawInput) {
      this.rawInput = rawInput;
      this.$refs.textareaProvider.syncValue(this.rawInput);
    },
    onlyWarnings() {
      const fields = Object.values(this.$refs.textareaObserver.fields).map(field => Object.keys(field.failedRules)).flat(Infinity);
      const someTest = fields.length ? !fields.some(failed => !this.warningValidators.includes(failed)) : false; // if there are no fields, return false because it means the field is validated silently and no failedRules are being recorded
      return someTest;
    },
    emitData(reset = false, off = false) {
      if (this.preventEmit) return;
      if (this.$refs.textareaObserver) {
        this.$refs.textareaObserver.validate({ silent: false })
          .then((valid) => {
            this.$emit('input', {
              valid: !this.toggled || ((valid || this.onlyWarnings()) && (this.schema.buttons ? this.buttons.valid : true) && (this.schema.actions ? this.actions.valid : true)),
              name: this.schema.returnKey || '',
              data: {
                value: whatsappFormatMessage(this.textValue).trim(),
                buttons: off ? this.schema.buttons : this.buttons,
                actions: off ? this.schema.actions : this.actions,
                toggle: this.toggled,
              },
            });
          })
          .finally(() => {
            if (reset) this.$refs.textareaObserver.reset();
          });
      } else {
        this.$emit('input', {
          valid: !this.toggled || ((this.schema.buttons ? this.buttons.valid : true) && (this.schema.actions ? this.actions.valid : true)),
          name: this.schema.returnKey || '',
          data: {
            value: whatsappFormatMessage(this.textValue).trim(),
            buttons: this.buttons,
            actions: this.actions,
            toggle: this.toggled,
          },
        });
      }
    },
    setToggle(state) {
      this.toggled = state;
      if (!state) {
        this.preventEmit = true;
        setTimeout(() => {
          this.toggleBackup.textValue = this.textValue;
          this.toggleBackup.buttons = this.buttons && this.buttons.data ? [...this.buttons.data.value] : [];
          this.toggleBackup.actions = this.actions && this.actions.data ? [...this.actions.data.value] : [];

          this.textValue = '';
          if (this.$refs.buttonsRef) this.$refs.buttonsRef.setData([]);
          if (this.$refs.actionsRef) this.$refs.actionsRef.setData([]);

          this.keyReset++;
          this.$nextTick(() => {
            this.preventEmit = false;
            this.emitData(false, true);

            if (this.$refs.textareaProvider) {
              this.$refs.textareaProvider.reset();
            }
            if (this.$refs.buttonsRef) this.$refs.buttonsRef.resetValidation();
            // if (this.$refs.actionsRef) this.$refs.actionsRef.resetValidation();
          });
        }, 500);
      } else {
        this.preventEmit = true;
        if (this.$refs.messageArea && this.toggleBackup.textValue) this.$refs.messageArea.setHtml(this.toggleBackup.textValue);
        if (this.toggleBackup.buttons && this.toggleBackup.buttons.length) this.$refs.buttonsRef.setData([...this.toggleBackup.buttons]);
        if (this.toggleBackup.actions && this.toggleBackup.actions.length) this.$refs.actionsRef.setData([...this.toggleBackup.actions]);
        this.$nextTick(() => {
          this.preventEmit = false;
          this.emitData();
          if (this.$refs.textareaProvider) {
            setTimeout(() => {
              this.$refs.textareaProvider.reset();
            }, 0);
            // if (this.$refs.actionsRef) this.$refs.actionsRef.resetValidation();
          }
          if (this.$refs.buttonsRef) this.$refs.buttonsRef.resetValidation();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-start-image {
  top: 1px;
  position: relative;
}

.wrap {
  border: 1px solid red;
  padding: 10px;

  padding: 17px 12px 18px;
  border-radius: 10px;
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  border: solid 1px $greyBorder;
  background-color: $greyUltralight;
}

.contact-message {
  margin-bottom: 10px;

  .contact-avatar {
    margin-top: 9px;
    margin-right: 8px;
  }
}

.AI-message {
  flex-direction: row-reverse;
  .AI-avatar {
    margin-top: 9px;
    margin-left: 8px;
    width: 18px;
    height: 18px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    @for $i from 1 through 6 {
    &.av-#{$i} {
      background-image: url('~@/assets/img/icons/assistant-avatars/av-#{$i}.svg');
    }
  }
  }
  .AI-input {
    width: 75%;
  }
}

.indent {
  padding-left: 50px !important;
}

::v-deep .text-input-wrapper {
  border-radius: 10px !important;
  min-height: unset !important;
}

::v-deep {
  .text-input-wrapper {
    min-height: calc(var(--height) + 36px) !important;
    // max-height: calc(var(--height) + 36px) !important;

    #vue-editor-wrapper {
      min-height: var(--height) !important;
      // max-height: var(--height) !important;
    }
  }
}

::v-deep {
  .border-15 .text-input-wrapper {
    border-radius: 15px !important;
  }
  .border-15.border-bottom-10 .text-input-wrapper {
    border-bottom-left-radius: 10px !important;
    border-bottom-right-radius: 10px !important;
  }
}

::v-deep .conversation-button-list {
  .button-fields:last-of-type .option input {
    border-bottom-left-radius: 15px !important;
    border-bottom-right-radius: 15px !important;
  }
}
</style>
